/**
 * Wall solids: centreline + thickness + openings → renderable panels.
 *
 * No CSG. An opening in a wall is always an axis-aligned rectangle in the wall's own
 * plane, so the wall is SPLIT into panels around it — exact geometry, clean UVs, and
 * no boolean library. Corners are mitred by intersecting neighbouring walls' offset
 * lines, which is what makes a room read as solid rather than as overlapping boxes.
 */

import type { Level, Opening, Surface, Vec2, Wall } from '../hp/types';

export interface WallPanel {
  wallId: string;
  /** Plan-space quad, ordered [leftStart, leftEnd, rightEnd, rightStart]. */
  quad: [Vec2, Vec2, Vec2, Vec2];
  /** Native joint footprint clipped to this panel's longitudinal span. */
  polygon: Vec2[];
  y0: number;
  y1: number;
  /** Distance along the wall centreline that the panel starts/ends, for U mapping. */
  u0: number;
  u1: number;
  kind: 'body' | 'under' | 'over' | 'pier';
}

export interface OpeningReveal {
  openingId: string;
  wallId: string;
  /** The four inner faces of the hole, each a plan quad plus its y range. */
  jambLeft: RevealFace;
  jambRight: RevealFace;
  sill: RevealFace;
  head: RevealFace;
  /** The opening's own frame in world terms, for placing a door/window model. */
  frame: {
    center: Vec2;
    /** Height of the opening centre above the floor. */
    centerY: number;
    width: number;
    height: number;
    /** Wall yaw in radians; the model's front faces the interior normal. */
    yaw: number;
    thickness: number;
  };
}

export interface RevealFace {
  quad: [Vec2, Vec2, Vec2, Vec2];
  y0: number;
  y1: number;
}

export interface WallSolid {
  wall: Wall;
  /** Shared by the plan and the extruded mesh, including bevels and multi-way tips. */
  outline: Vec2[];
  /** Mitred plan corners: left/right are the two sides at start and end. */
  corners: { leftStart: Vec2; leftEnd: Vec2; rightStart: Vec2; rightEnd: Vec2 };
  panels: WallPanel[];
  reveals: OpeningReveal[];
  /** Which surface faces the room interior. */
  interior: Surface | undefined;
  exterior: Surface | undefined;
  /** True when `normalIn` points at the wall's `left` side (start→end +90° is `right`). */
  interiorIsLeft: boolean;
}

const EPS = 0.001;

function sub(a: Vec2, b: Vec2): Vec2 { return { x: a.x - b.x, z: a.z - b.z }; }
function add(a: Vec2, b: Vec2): Vec2 { return { x: a.x + b.x, z: a.z + b.z }; }
function mul(a: Vec2, k: number): Vec2 { return { x: a.x * k, z: a.z * k }; }
function len(a: Vec2): number { return Math.hypot(a.x, a.z); }
function norm(a: Vec2): Vec2 { const l = len(a) || 1; return { x: a.x / l, z: a.z / l }; }
function lerp(a: Vec2, b: Vec2, t: number): Vec2 { return { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t }; }
/** +90° from `d` in plan space (the wall's `right` side). */
function perp(d: Vec2): Vec2 { return { x: -d.z, z: d.x }; }
function near(a: Vec2, b: Vec2, eps = EPS): boolean { return Math.abs(a.x - b.x) < eps && Math.abs(a.z - b.z) < eps; }

/** Intersection of line (p, dir) with line (q, e); null when near-parallel. */
function intersect(p: Vec2, d: Vec2, q: Vec2, e: Vec2): Vec2 | null {
  const den = d.x * e.z - d.z * e.x;
  if (Math.abs(den) < 1e-6) return null;
  const t = ((q.x - p.x) * e.z - (q.z - p.z) * e.x) / den;
  return add(p, mul(d, t));
}

const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.z * b.z;

/** Clip to dot(p − origin, inward) ≥ 0, preserving a simple convex footprint. */
function clip(poly: Vec2[], origin: Vec2, inward: Vec2): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const da = dot(sub(a, origin), inward), db = dot(sub(b, origin), inward);
    const aIn = da >= -1e-9, bIn = db >= -1e-9;
    if (aIn) out.push(a);
    if (aIn !== bIn) out.push(lerp(a, b, da / (da - db)));
  }
  return cleanPolygon(out);
}

function cleanPolygon(points: Vec2[]): Vec2[] {
  const poly = points.map(p => ({ x: Math.abs(p.x) < 1e-10 ? 0 : p.x, z: Math.abs(p.z) < 1e-10 ? 0 : p.z }));
  for (let i = poly.length - 1; i >= 0 && poly.length > 2; i--) {
    const a = poly[(i + poly.length - 1) % poly.length], b = poly[i], c = poly[(i + 1) % poly.length];
    const ab = sub(b, a), bc = sub(c, b);
    if (len(ab) < 1e-8 || Math.abs(ab.x * bc.z - ab.z * bc.x) < 1e-10) poly.splice(i, 1);
  }
  return poly;
}

function onRoomContour(wall: Wall, polygon: Vec2[]): boolean {
  return polygon.some((a, i) => {
    const b = polygon[(i + 1) % polygon.length], edge = sub(b, a), length = len(edge);
    if (length < EPS) return false;
    const direction = mul(edge, 1 / length);
    return [wall.start, wall.end].every(point => {
      const relative = sub(point, a), along = dot(relative, direction);
      return along >= -EPS && along <= length + EPS
        && Math.abs(relative.x * direction.z - relative.z * direction.x) < EPS;
    });
  });
}

function compatibleNeighbors(wall: Wall, other: Wall, level: Level): boolean {
  if ((wall.height >= level.height) === (other.height >= level.height)) return true;
  // Unity keeps contour neighbors connected across height classes; merely owning a room is not enough.
  return level.rooms.some(room => room.polygon.length >= 3
    && onRoomContour(wall, room.polygon) && onRoomContour(other, room.polygon));
}

/**
 * Port of WallSeamlessConnector.CalculateTipPoints/GenerateMeshPoints: each end
 * looks OUT along its wall, chooses the previous/next angular neighbour, and cuts
 * its strip by those two tip segments. The centre point partitions T/four-way
 * junctions instead of stacking coplanar caps. Acute outer mitres are bevelled.
 */
function wallOutline(wall: Wall, level: Level): Vec2[] {
  const walls = level.walls;
  const d = norm(sub(wall.end, wall.start)), n = perp(d), h = wall.thickness / 2;
  const pad = Math.max(wall.thickness, ...walls.map(w => w.thickness)) * 2;
  let polygon = [
    add(add(wall.start, mul(d, -pad)), mul(n, -h)),
    add(add(wall.end, mul(d, pad)), mul(n, -h)),
    add(add(wall.end, mul(d, pad)), mul(n, h)),
    add(add(wall.start, mul(d, -pad)), mul(n, h)),
  ];
  for (const [at, outgoing] of [[wall.start, d], [wall.end, mul(d, -1)]] as const) {
    const normal = perp(outgoing), angle = Math.atan2(outgoing.z, outgoing.x);
    const rays = walls.filter(w => w.id !== wall.id && !w.hidden && (near(w.start, at) || near(w.end, at))
      && compatibleNeighbors(wall, w, level)).map(w => {
      const direction = norm(sub(near(w.start, at) ? w.end : w.start, at));
      const delta = (Math.atan2(direction.z, direction.x) - angle + Math.PI * 2) % (Math.PI * 2);
      return { direction, half: w.thickness / 2, delta };
    });
    const bevels: Array<{ origin: Vec2; inward: Vec2 }> = [];
    const tip = (side: 1 | -1): Vec2 => {
      const base = add(at, mul(normal, side * h));
      const adjacent = rays.map(ray => ({ ...ray, gap: side === 1 ? ray.delta : Math.PI * 2 - ray.delta }))
        .filter(ray => ray.gap > 1e-8).sort((a, b) => a.gap - b.gap)[0];
      if (!adjacent || Math.abs(Math.sin(adjacent.gap)) < .01) return base;
      const otherBase = add(at, mul(perp(adjacent.direction), -side * adjacent.half));
      const point = intersect(base, outgoing, otherBase, adjacent.direction) ?? base;
      // A mitre tip that reaches back past the joint further than a square corner would — an acute
      // corner, or a thick wall meeting a thin one at a shallow angle — is chamfered at that bound,
      // so no wall grows a wedge along its neighbour. Right corners stay exact, whatever the two
      // thicknesses: the bound is measured from the thicker wall.
      const bound = Math.SQRT2 * Math.max(h, adjacent.half);
      if (dot(sub(point, at), outgoing) < 0 && len(sub(point, at)) > bound * 1.15) {
        const outward = norm(sub(point, at));
        bevels.push({ origin: add(at, mul(outward, bound)), inward: mul(outward, -1) });
      }
      return point;
    };
    const tips = [tip(-1), at, tip(1)];
    for (let i = 0; i < 2; i++) {
      const edge = sub(tips[i + 1], tips[i]);
      if (len(edge) < 1e-9) continue;
      let inward = perp(edge);
      if (dot(outgoing, inward) < 0) inward = mul(inward, -1);
      polygon = clip(polygon, tips[i], inward);
    }
    for (const bevel of bevels) polygon = clip(polygon, bevel.origin, bevel.inward);
  }
  return cleanPolygon(polygon);
}

interface SolidsCacheEntry { rooms: Level['rooms']; height: number; solids: WallSolid[] }
const solidsCache = new WeakMap<Level['walls'], WeakMap<Level['openings'], SolidsCacheEntry>>();
export function buildWallSolids(level: Level): WallSolid[] {
  let byOpenings = solidsCache.get(level.walls);
  if (!byOpenings) { byOpenings = new WeakMap(); solidsCache.set(level.walls, byOpenings); }
  let entry = byOpenings.get(level.openings);
  if (!entry || entry.rooms !== level.rooms || entry.height !== level.height) {
    entry = { rooms: level.rooms, height: level.height, solids: computeWallSolids(level) };
    byOpenings.set(level.openings, entry);
  }
  return entry.solids;
}
function computeWallSolids(level: Level): WallSolid[] {
  const walls = level.walls;
  return walls.map((wall) => {
    const d = norm(sub(wall.end, wall.start));
    const n = perp(d);                          // the wall's `right` side
    const h = wall.thickness / 2;

    const outline = wallOutline(wall, level);
    const sideEnds = (sign: 1 | -1): [Vec2, Vec2] => {
      const points = outline.filter(p => Math.abs(dot(sub(p, wall.start), n) - sign * h) < 1e-7)
        .sort((a,b) => dot(sub(a,b),d));
      return [points[0] ?? add(wall.start,mul(n,sign*h)), points.at(-1) ?? add(wall.end,mul(n,sign*h))];
    };
    const [rightStart,rightEnd] = sideEnds(1), [leftStart,leftEnd] = sideEnds(-1);
    const corners = {
      rightStart, rightEnd, leftStart, leftEnd,
    };

    // Does the interior normal point at the `left` side?
    const interiorIsLeft = (wall.normalIn.x * n.x + wall.normalIn.z * n.z) < 0;

    const openings = level.openings
      .filter((o) => o.wallId === wall.id)
      .slice()
      .sort((a, b) => a.offset - b.offset);

    const panels: WallPanel[] = [];
    const reveals: OpeningReveal[] = [];
    const L = wall.length || len(sub(wall.end, wall.start)) || 1;

    /** Plan quad for the span [u0, u1] of the wall, interpolating the mitred corners. */
    const spanQuad = (u0: number, u1: number): [Vec2, Vec2, Vec2, Vec2] => {
      const at = (u:number,sign:1|-1) => add(add(wall.start,mul(d,u)),mul(n,sign*h));
      return [
        u0===0 ? leftStart : at(u0,-1),
        u1===L ? leftEnd : at(u1,-1),
        u1===L ? rightEnd : at(u1,1),
        u0===0 ? rightStart : at(u0,1),
      ];
    };
    const pushPanel = (u0:number,u1:number,y0:number,y1:number,kind:WallPanel['kind']) => {
      let polygon=outline;
      if(u0>1e-8)polygon=clip(polygon,add(wall.start,mul(d,u0)),d);
      if(u1<L-1e-8)polygon=clip(polygon,add(wall.start,mul(d,u1)),mul(d,-1));
      if(polygon.length>=3)panels.push({wallId:wall.id,quad:spanQuad(u0,u1),polygon,y0,y1,u0,u1,kind});
    };

    let cursor = 0;
    for (const o of openings) {
      const a = Math.max(0, o.offset - o.width / 2);
      const b = Math.min(L, o.offset + o.width / 2);
      if (b <= a) continue;

      if (a - cursor > 1e-3) {
        pushPanel(cursor,a,0,wall.height,cursor===0?'body':'pier');
      }
      if (o.sill > 1e-3) {
        pushPanel(a,b,0,o.sill,'under');
      }
      const top = o.sill + o.height;
      if (wall.height - top > 1e-3) {
        pushPanel(a,b,top,wall.height,'over');
      }
      reveals.push(buildReveal(o, wall, corners, L, a, b, spanQuad, interiorIsLeft));
      cursor = b;
    }
    if (L - cursor > 1e-3) {
      pushPanel(cursor,L,0,wall.height,cursor===0?'body':'pier');
    }

    return {
      wall,
      outline,
      corners,
      panels,
      reveals,
      interior: interiorIsLeft ? wall.frontSurface : wall.frontSurface,
      exterior: wall.backSurface,
      interiorIsLeft,
    };
  });
}

function buildReveal(
  o: Opening,
  wall: Wall,
  corners: WallSolid['corners'],
  L: number,
  a: number,
  b: number,
  spanQuad: (u0: number, u1: number) => [Vec2, Vec2, Vec2, Vec2],
  interiorIsLeft: boolean,
): OpeningReveal {
  const qa = spanQuad(a, a);
  const qb = spanQuad(b, b);
  // qa = [leftA, leftA, rightA, rightA] (degenerate span) — take the distinct corners.
  const leftA = qa[0]; const rightA = qa[3];
  const leftB = qb[0]; const rightB = qb[3];
  const top = o.sill + o.height;

  const face = (p0: Vec2, p1: Vec2, y0: number, y1: number): RevealFace => ({
    quad: [p0, p1, p1, p0],
    y0,
    y1,
  });

  const mid = lerp(lerp(leftA, rightA, 0.5), lerp(leftB, rightB, 0.5), 0.5);
  const dir = norm(sub(wall.end, wall.start));
  const yaw = Math.atan2(wall.normalIn.x, wall.normalIn.z);

  return {
    openingId: o.id,
    wallId: wall.id,
    jambLeft: face(leftA, rightA, o.sill, top),
    jambRight: face(leftB, rightB, o.sill, top),
    sill: { quad: [leftA, leftB, rightB, rightA], y0: o.sill, y1: o.sill },
    head: { quad: [leftA, leftB, rightB, rightA], y0: top, y1: top },
    frame: {
      center: mid,
      centerY: o.sill + o.height / 2,
      width: o.width,
      height: o.height,
      yaw,
      thickness: wall.thickness,
    },
  };
}

/**
 * The edges of each wall's outline that lie on the boundary of the walls' union: the seam
 * between two polygons meeting at a joint, and the part of a thin wall buried in a thick one,
 * are inside the solid and are not drawn. Pieces are keyed by wall so a highlighted wall can
 * still show its whole outline.
 */
export function visibleOutlineEdges(solids: WallSolid[]): Map<string, Array<[Vec2, Vec2]>> {
  const result = new Map<string, Array<[Vec2, Vec2]>>();
  const polygons = solids.map(solid => solid.outline);
  for (const [index, solid] of solids.entries()) {
    const polygon = polygons[index], pieces: Array<[Vec2, Vec2]> = [];
    const area = polygon.reduce((sum, p, i) => sum + (p.x * polygon[(i + 1) % polygon.length].z - polygon[(i + 1) % polygon.length].x * p.z), 0);
    for (let i = 0; i < polygon.length; i++) {
      const a = polygon[i], b = polygon[(i + 1) % polygon.length], edge = sub(b, a), span = len(edge);
      if (span < 1e-9) continue;
      // Outward is away from this polygon's interior, whichever way it is wound.
      const outward = mul(perp(edge), (area > 0 ? -1 : 1) / span);
      const cuts = [0, 1];
      for (const [other, otherPolygon] of polygons.entries()) {
        if (other === index) continue;
        for (let j = 0; j < otherPolygon.length; j++) {
          const c = otherPolygon[j], d = otherPolygon[(j + 1) % otherPolygon.length];
          const r = edge, s = sub(d, c), den = r.x * s.z - r.z * s.x;
          if (Math.abs(den) < 1e-12) continue;
          const t = ((c.x - a.x) * s.z - (c.z - a.z) * s.x) / den, u = ((c.x - a.x) * r.z - (c.z - a.z) * r.x) / den;
          if (t > 1e-9 && t < 1 - 1e-9 && u >= -1e-9 && u <= 1 + 1e-9) cuts.push(t);
        }
      }
      cuts.sort((p, q) => p - q);
      for (let k = 1; k < cuts.length; k++) {
        if (cuts[k] - cuts[k - 1] < 1e-9) continue;
        const from = lerp(a, b, cuts[k - 1]), to = lerp(a, b, cuts[k]);
        const probe = add(lerp(from, to, .5), mul(outward, 1e-4));
        if (!polygons.some((otherPolygon, other) => other !== index && pointInPolygon(otherPolygon, probe))) pieces.push([from, to]);
      }
    }
    result.set(solid.wall.id, pieces);
  }
  return result;
}

/** The parts of the segment a→b that lie outside every polygon: a dimension line stops at a wall instead of crossing it. */
export function segmentOutside(a: Vec2, b: Vec2, polygons: Vec2[][]): Array<[Vec2, Vec2]> {
  const edge = sub(b, a);
  if (len(edge) < 1e-9) return [];
  const cuts = [0, 1];
  for (const polygon of polygons) for (let j = 0; j < polygon.length; j++) {
    const c = polygon[j], d = polygon[(j + 1) % polygon.length], s = sub(d, c), den = edge.x * s.z - edge.z * s.x;
    if (Math.abs(den) < 1e-12) continue;
    const t = ((c.x - a.x) * s.z - (c.z - a.z) * s.x) / den, u = ((c.x - a.x) * edge.z - (c.z - a.z) * edge.x) / den;
    if (t > 1e-9 && t < 1 - 1e-9 && u >= -1e-9 && u <= 1 + 1e-9) cuts.push(t);
  }
  cuts.sort((p, q) => p - q);
  const pieces: Array<[Vec2, Vec2]> = [];
  for (let k = 1; k < cuts.length; k++) {
    if (cuts[k] - cuts[k - 1] < 1e-9) continue;
    const from = lerp(a, b, cuts[k - 1]), to = lerp(a, b, cuts[k]);
    if (!polygons.some(polygon => pointInPolygon(polygon, lerp(from, to, .5)))) pieces.push([from, to]);
  }
  return pieces;
}

function pointInPolygon(polygon: Vec2[], p: Vec2): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i], b = polygon[j];
    if ((a.z > p.z) !== (b.z > p.z) && p.x < (b.x - a.x) * (p.z - a.z) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

/** Total floor area covered by the level's rooms, for sanity reporting. */
export function levelArea(level: Level): number {
  return level.rooms.reduce((s, r) => s + r.area, 0);
}
